import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Container Component
 * Based on UX Design Guidelines §5.4 - Layout Grid
 *
 * Variants:
 * - narrow: 480px - Forms, modals
 * - medium: 640px - Settings pages
 * - wide: 800px - Content pages
 * - full: 1024px - Dashboard layouts
 * - max: 1280px - Wide layouts
 */

export type ContainerSize = 'narrow' | 'medium' | 'wide' | 'full' | 'max'

export interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  /** Maximum width variant */
  size?: ContainerSize
  /** Add horizontal padding */
  padding?: boolean
  /** Center the container */
  center?: boolean
  children: ReactNode
}

const sizeStyles: Record<ContainerSize, string> = {
  narrow: 'max-w-narrow', // 480px
  medium: 'max-w-medium', // 640px
  wide: 'max-w-wide', // 800px
  full: 'max-w-container', // 1024px
  max: 'max-w-container-lg', // 1280px
}

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      size = 'max',
      padding = true,
      center = true,
      className,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'w-full',
          sizeStyles[size],
          center && 'mx-auto',
          padding && 'px-4 sm:px-6 lg:px-8',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Container.displayName = 'Container'

/**
 * PageContainer - Full-width container for page content
 * Includes vertical padding for consistent page spacing
 */
export interface PageContainerProps extends ContainerProps {
  /** Vertical padding size */
  verticalPadding?: 'sm' | 'md' | 'lg'
}

const verticalPaddingStyles = {
  sm: 'py-2 sm:py-3',
  md: 'py-3 sm:py-4',
  lg: 'py-4 sm:py-6',
}

export const PageContainer = forwardRef<HTMLDivElement, PageContainerProps>(
  ({ verticalPadding = 'md', className, children, ...props }, ref) => {
    return (
      <Container
        ref={ref}
        className={cn(verticalPaddingStyles[verticalPadding], className)}
        {...props}
      >
        {children}
      </Container>
    )
  }
)

PageContainer.displayName = 'PageContainer'

/**
 * CenteredContent - Centers content both horizontally and vertically
 * Useful for auth pages, empty states, error pages
 */
export interface CenteredContentProps extends HTMLAttributes<HTMLDivElement> {
  /** Minimum height */
  minHeight?: string
  children: ReactNode
}

export const CenteredContent = forwardRef<HTMLDivElement, CenteredContentProps>(
  ({ minHeight = '400px', className, children, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center',
          'w-full px-4',
          className
        )}
        style={{ minHeight, ...style }}
        {...props}
      >
        {children}
      </div>
    )
  }
)

CenteredContent.displayName = 'CenteredContent'
